import { AxiosError } from "axios";

/**
 * Standard API error response structure
 */
export interface ApiErrorResponse {
  error: string;
  message: string;
  status: number;
  path: string;
  timestamp: string;
}

/**
 * Processed error information for display
 */
export interface ProcessedError {
  title: string;
  message: string;
  status?: number;
  type: 'access_denied' | 'not_found' | 'unauthorized' | 'forbidden' | 'server_error' | 'network_error' | 'unknown';
  canRetry: boolean;
}

/**
 * Type guard to check if error is an AxiosError
 */
export function isAxiosError(error: unknown): error is AxiosError {
  return (
    typeof error === 'object' &&
    error !== null &&
    'isAxiosError' in error &&
    (error as AxiosError).isAxiosError === true
  );
}

/**
 * Type guard to check if error is a standard Error
 */
export function isError(error: unknown): error is Error {
  return error instanceof Error;
}

/**
 * Type guard to check if response data matches API error structure
 */
function isApiErrorResponse(data: unknown): data is ApiErrorResponse {
  return (
    typeof data === 'object' &&
    data !== null &&
    'error' in data && // Some APIs might use 'error' instead of 'message'
    'message' in data
  );
}

/**
 * Extract error message safely from any error type
 */
export function getErrorMessage(error: unknown): string {
  if (isAxiosError(error)) {
    // Try to get the most specific error message
    const responseData = error.response?.data;

    // Handle different response data structures
    if (responseData) {
      // Try common message fields
      if (typeof responseData === 'object' && responseData !== null) {
        const data = responseData as Record<string, unknown>;

        // Check for message field
        if (typeof data.message === 'string' && data.message) {
          return data.message;
        }

        // Check for error field
        if (typeof data.error === 'string' && data.error) {
          return data.error;
        }

        // Check for detail field (some APIs use this)
        if (typeof data.detail === 'string' && data.detail) {
          return data.detail;
        }
      }

      // If response data is a string, use it
      if (typeof responseData === 'string' && responseData) {
        return responseData;
      }
    }

    // Fallback to error message or generic message
    return error.message || 'Network error occurred';
  }

  if (isError(error)) {
    return error.message;
  }

  if (typeof error === 'string') {
    return error;
  }

  return 'An unknown error occurred';
}

/**
 * Get HTTP status code from error
 */
export function getErrorStatus(error: unknown): number | undefined {
  if (isAxiosError(error)) {
    return error.response?.status;
  }
  return undefined;
}

/**
 * Check if error is a specific HTTP status
 */
export function isErrorStatus(error: unknown, status: number): boolean {
  return getErrorStatus(error) === status;
}

/**
 * Check if error is a network error (no response received)
 */
export function isNetworkError(error: unknown): boolean {
  return isAxiosError(error) && !error.response;
}

/**
 * Check if error is an access denied error (404 with "Access Denied" message)
 */
export function isAccessDeniedError(error: unknown): boolean {
  if (!isAxiosError(error)) return false;

  const status = error.response?.status;
  const data = error.response?.data;

  if (status === 404 && isApiErrorResponse(data)) {
    return data.message === 'Access Denied: You do not have permission to access this resource. Please contact your administrator if you believe this is an error.' || data.error === 'NOT_FOUND';
  }

  return false;
}

/**
 * Process error and return user-friendly information
 * This is the main function to use for centralized error handling
 */
export function processError(error: unknown): ProcessedError {
  // Handle network errors
  if (isNetworkError(error)) {
    return {
      title: 'Network Error',
      message: 'Unable to connect to the server. Please check your internet connection and try again.',
      type: 'network_error',
      canRetry: true,
    };
  }

  // Handle Axios errors with response
  if (isAxiosError(error) && error.response) {
    const status = error.response.status;
    const data = error.response.data;
    const apiError = isApiErrorResponse(data) ? data : null;

    // Handle 404 with "Access Denied" - special case
    if (isAccessDeniedError(error)) {
      return {
        title: 'Access Denied',
        message: 'You do not have permission to access this resource. Please contact your administrator if you believe this is an error.',
        status: 404,
        type: 'access_denied',
        canRetry: false,
      };
    }

    // Handle other status codes
    switch (status) {
      case 400:
        return {
          title: 'Invalid Request',
          message: apiError?.message || 'The request contains invalid data. Please check your input and try again.',
          status,
          type: 'unknown',
          canRetry: false,
        };

      case 401:
        return {
          title: 'Unauthorized',
          message: 'Your session has expired. Please log in again.',
          status,
          type: 'unauthorized',
          canRetry: false,
        };

      case 403:
        return {
          title: 'Forbidden',
          message: apiError?.message || 'You do not have permission to perform this action.',
          status,
          type: 'forbidden',
          canRetry: false,
        };

      case 404:
        return {
          title: 'Not Found',
          message: apiError?.message || 'The requested resource was not found.',
          status,
          type: 'not_found',
          canRetry: false,
        };

      case 409:
        return {
          title: 'Conflict',
          message: apiError?.message || 'This operation conflicts with existing data.',
          status,
          type: 'unknown',
          canRetry: false,
        };

      case 422:
        return {
          title: 'Validation Error',
          message: apiError?.message || 'The data provided failed validation.',
          status,
          type: 'unknown',
          canRetry: false,
        };

      case 500:
      case 502:
      case 503:
      case 504:
        return {
          title: 'Server Error',
          message: 'An error occurred on the server. Please try again later.',
          status,
          type: 'server_error',
          canRetry: true,
        };

      default:
        return {
          title: 'Error',
          message: apiError?.message || 'An unexpected error occurred. Please try again.',
          status,
          type: 'unknown',
          canRetry: true,
        };
    }
  }

  // Handle standard Error objects
  if (isError(error)) {
    return {
      title: 'Error',
      message: error.message,
      type: 'unknown',
      canRetry: false,
    };
  }

  // Handle string errors
  if (typeof error === 'string') {
    return {
      title: 'Error',
      message: error,
      type: 'unknown',
      canRetry: false,
    };
  }

  // Fallback for unknown error types
  return {
    title: 'Unknown Error',
    message: 'An unexpected error occurred. Please try again.',
    type: 'unknown',
    canRetry: false,
  };
}

/**
 * Display error using toast notification
 * Usage: displayError(error, toast)
 */
export function displayError(error: unknown, toast: (options: { title: string; description: string; variant?: 'default' | 'destructive' }) => void): void {
  const processedError = processError(error);

  toast({
    title: processedError.title,
    description: processedError.message,
    variant: 'destructive',
  });
}

/**
 * Get user-friendly error message for display
 */
export function getUserFriendlyErrorMessage(error: unknown): string {
  const processed = processError(error);
  return `${processed.title}: ${processed.message}`;
}

